import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { hasRole } from '../config/roles';
import { getCurrentUser } from '../utils/api';
import useRolePermissions, { canAccessRoute } from '../hooks/useRolePermissions';

// Hardcoded per explicit request. Note: a PIN baked into the shipped
// frontend bundle is visible to anyone who opens devtools/view-source, so
// this is a soft/UX gate on top of the real protection (the role check
// below, which already ran server-verified data before this component
// renders at all) -- not a substitute for it.
const MEMBER_DB_PIN = '40219';

// Verified once per browser session (cleared on logout via Sidebar's
// sessionStorage.clear(), and naturally on tab/browser close) -- matches
// the "Resets every session" claim already shown below, which the PIN
// modal wasn't actually honoring: `showPinModal` started at `true` on
// every mount, so leaving this route and coming back re-prompted every
// single time instead of once per session.
const PIN_VERIFIED_KEY = 'memberDbPinVerified';

const PinInput = ({ length = 5, value = '', onChange }) => {
  const inputRefs = React.useRef([]);

  const handleChange = (e, index) => {
    const newValue = e.target.value.replace(/[^0-9]/g, '');
    if (newValue) {
      const newPin = value.split('');
      newPin[index] = newValue[newValue.length - 1];
      onChange(newPin.join('').substring(0, length));
      if (index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="password"
          inputMode="numeric"
          className="num"
          style={{
            width: 46,
            height: 56,
            fontSize: 22,
            textAlign: 'center',
            background: 'var(--surface-2)',
            border: `1px solid ${value[index] ? 'var(--accent)' : 'var(--border)'}`,
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text-1)',
            outline: 'none',
            transition: 'border-color .15s ease, box-shadow .15s ease',
            boxShadow: value[index] ? '0 0 0 3px rgba(124,58,237,0.18)' : 'none',
          }}
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          autoFocus={index === 0}
        />
      ))}
    </div>
  );
};

const SHIELD_SVG = (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 3l7 3v6c0 4.5-3 8-7 9-4-1-7-4.5-7-9V6l7-3Z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

/** The three protections genuinely in place here -- no overstated claims. */
const SECURITY_CHECKS = [
  { label: 'Officer role verified', done: true },
  { label: 'PIN gate on this page', done: true },
  { label: 'Resets every session', done: true },
];

export default function PinProtectedRoute({ allowedRoles, children, dynamicPathKey }) {
  const [pin, setPin] = useState('');
  const [showPinModal, setShowPinModal] = useState(() => {
    try {
      return sessionStorage.getItem(PIN_VERIFIED_KEY) !== 'true';
    } catch {
      return true;
    }
  });
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const navigate = useNavigate();
  const user = getCurrentUser();
  // Same optional dynamicPathKey mechanism as ProtectedRoute.jsx -- see its
  // docblock for the fallback semantics. The PIN gate below is unaffected
  // either way: it's an additional layer that only ever runs after this
  // role check passes, same as it always has.
  const perm = useRolePermissions(user?.role, { enabled: !!dynamicPathKey });

  const handleClose = () => navigate('/home');

  // Check if user is authenticated and has the right role
  if (!user) return <Navigate to="/login" />;

  if (!dynamicPathKey) {
    if (!hasRole(user.role, allowedRoles)) {
      return <Navigate to="/unauthorized" />;
    }
  } else {
    if (perm.loading) return null;
    if (!canAccessRoute(dynamicPathKey, perm, user.role, allowedRoles)) {
      return <Navigate to="/unauthorized" />;
    }
  }

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pin.length < 5) {
      setError('Enter all 5 digits.');
      return;
    }
    if (pin === MEMBER_DB_PIN) {
      try {
        sessionStorage.setItem(PIN_VERIFIED_KEY, 'true');
      } catch {
        /* non-critical -- just means it'll ask again next visit */
      }
      setShowPinModal(false);
    } else {
      setError('Incorrect PIN. Please try again.');
      setPin('');
      setShake(true);
      setTimeout(() => setShake(false), 400);
    }
  };

  if (showPinModal) {
    return (
      <>
        <style>{`
          @keyframes leo-pin-shake {
            10%, 90% { transform: translateX(-1px); }
            20%, 80% { transform: translateX(2px); }
            30%, 50%, 70% { transform: translateX(-4px); }
            40%, 60% { transform: translateX(4px); }
          }
        `}</style>
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 999,
            background: 'rgba(5,7,12,0.8)', backdropFilter: 'blur(6px)',
            display: 'grid', placeItems: 'center', padding: 16,
          }}
        >
          <div
            className="panel"
            style={{
              position: 'relative', width: '100%', maxWidth: 400, padding: 32,
              animation: shake ? 'leo-pin-shake .4s ease' : 'none',
            }}
          >
            <button
              onClick={handleClose}
              className="icon-btn"
              style={{ position: 'absolute', top: 14, right: 14 }}
              aria-label="Close"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: 52, height: 52, borderRadius: 14, margin: '0 auto 16px',
                  display: 'grid', placeItems: 'center',
                  background: 'var(--accent)', color: '#fff',
                  boxShadow: '0 12px 30px -12px rgba(124,58,237,.6)',
                }}
              >
                {SHIELD_SVG}
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-1)' }}>Restricted Access</h3>
              <p style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 6, marginBottom: 24 }}>
                The Member Directory contains personal information. Enter the security PIN to continue.
              </p>
            </div>

            <form onSubmit={handlePinSubmit}>
              <PinInput length={5} value={pin} onChange={(v) => { setPin(v); setError(''); }} />
              {error && (
                <div style={{ textAlign: 'center', color: 'var(--error)', fontSize: 12.5, marginTop: 12 }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', marginTop: 22 }}
              >
                Verify &amp; Continue
              </button>
            </form>

            <div
              style={{
                marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)',
                display: 'flex', flexDirection: 'column', gap: 8,
              }}
            >
              <div className="eyebrow" style={{ marginBottom: 2 }}>This page is protected by</div>
              {SECURITY_CHECKS.map((c) => (
                <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-2)' }}>
                  <span
                    style={{
                      width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                      background: 'var(--success)', color: '#fff',
                      display: 'grid', placeItems: 'center',
                    }}
                  >
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M20 6L9 17l-5-5" />
                    </svg>
                  </span>
                  {c.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  return children;
}
